import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/pages/HomePage.css";
import "../styles/pages/DesktopStorefront.css";
import CategorySection from "../components/home/CategorySection";
import ProductCard from "../components/product/ProductCard";
import LoadingState from "../components/shared/LoadingState";
import DesktopHomePage from "../components/home/DesktopHomePage";
import WhyShopSection from "../components/home/WhyShopSection";
import OfferBannerStrip from "../components/home/OfferBannerStrip";
import { useProducts } from "../contexts/ProductContext";
import { useAuth } from "../contexts/AuthContext";
import { Banner, BrandLogo, CategorySummary, HomepageSectionContent, Product } from "../types/store";
import { fetchBanners, fetchOfferBanners, fetchOfferCards } from "../services/bannerService";
import { fetchBrandLogos } from "../services/brandLogoService";
import { fetchHomepageSections } from "../services/homepageSectionService";
import { fetchWallet } from "../services/accountService";
import { resolveMediaUrl } from "../utils/mediaUrl";
import { getHomepageSectionProducts } from "../utils/homepageSections";
import { useProgressiveProductReveal } from "../hooks/useProgressiveProductReveal";
import bannerOne from "../assets/banners/ban1.png";
import walletIcon from "../assets/wallet.png";
import offerQualityImage from "../assets/offers/quality.png";
import offerCodImage from "../assets/offers/cod.png";
import offerDeliveryImage from "../assets/offers/delivery.png";
import offerCashbackImage from "../assets/offers/cashback.png";

interface ProductCarouselSectionProps {
  title: string;
  eyebrow?: string;
  products: Product[];
  loading: boolean;
  seeAllTo: string;
}

interface HomeResolvedSection {
  sectionKey: string;
  eyebrow?: string;
  title: string;
  products: Product[];
  kind: "products" | "categories" | "brands" | "offer-cards" | "offer-banners";
}

const buildSectionLink = (section: Pick<HomeResolvedSection, "sectionKey" | "title">) => {
  const params = new URLSearchParams({
    discover: "1",
    view: "collection",
    section: section.sectionKey,
    title: section.title,
  });

  return `/products?${params.toString()}`;
};

const mobileOfferTiles = [
  { label: "Top Quality", image: offerQualityImage },
  { label: "COD Available", image: offerCodImage },
  { label: "Fast Delivery", image: offerDeliveryImage },
  { label: "Cashbacks", image: offerCashbackImage },
];

const defaultHomepageSections: HomepageSectionContent[] = [
  { sectionKey: "categories", heading: "Shop by Category" },
  { sectionKey: "hard-to-find", heading: "Hard-to-Find Products" },
  { sectionKey: "everyday-essentials", heading: "Everyday Essentials" },
  { sectionKey: "shop-by-brand", heading: "Shop by Brand" },
  { sectionKey: "electrical-essentials", heading: "Electrical Essentials" },
  { sectionKey: "hardware-tools", heading: "Hardware & Tools" },
  { sectionKey: "plumbing-bathroom", heading: "Plumbing & Bathroom" },
  { sectionKey: "recently-added", heading: "Recently Added" },
  { sectionKey: "best-selling", heading: "Best-Selling Products" },
];

interface MobileOfferTile {
  label: string;
  image: string;
}

const MobileReferenceHome: React.FC<{
  categories: CategorySummary[];
  offerTiles: MobileOfferTile[];
  banners: Banner[];
}> = ({
  categories,
  offerTiles,
  banners,
}) => {
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const featuredCategories = categories
    .filter((category) => category.slug !== "services" && category.name.toLowerCase() !== "services")
    .slice(0, 8);
  const heroBanners = banners.length
    ? banners.filter((banner) => banner.imageUrl)
    : [{ id: 0, imageUrl: bannerOne, heading: "Everything your home needs all in one place", slug: "popular" }];

  useEffect(() => {
    setActiveBannerIndex(0);
  }, [heroBanners.length]);

  useEffect(() => {
    if (heroBanners.length <= 1) {
      return undefined;
    }

    const rotationId = window.setInterval(() => {
      setActiveBannerIndex((current) => (current + 1) % heroBanners.length);
    }, 4200);

    return () => window.clearInterval(rotationId);
  }, [heroBanners.length]);

  const handleBannerTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleBannerTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || heroBanners.length <= 1) {
      return;
    }

    const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const deltaX = touchEndX - touchStartX.current;
    if (Math.abs(deltaX) > 40) {
      setActiveBannerIndex((current) =>
        deltaX < 0
          ? (current + 1) % heroBanners.length
          : (current - 1 + heroBanners.length) % heroBanners.length,
      );
    }

    touchStartX.current = null;
  };

  return (
    <section className="mobile-reference-home" aria-label="Eldoo home">
      <nav className="mobile-reference-home__tabs" aria-label="Featured shortcuts">
        <Link className="is-active" to="/">
          <span className="mobile-reference-home__tab-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M7 8V7a5 5 0 0 1 10 0v1h2.2l1 12H3.8l1-12H7Zm2 0h6V7a3 3 0 0 0-6 0v1Zm-2.36 2-.66 8h12.04l-.66-8H16v2h-2v-2h-4v2H8v-2H6.64Z" />
            </svg>
          </span>
          <span>Shop</span>
        </Link>
        <Link to="/services">
          <span className="mobile-reference-home__tab-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="m21 7.65-2.82 2.82-2.65-2.65L18.35 5A5.45 5.45 0 0 0 12 11.7L4.3 19.4a1.84 1.84 0 0 0 2.6 2.6l7.7-7.7A5.45 5.45 0 0 0 21 7.65ZM5.6 20.7a.28.28 0 0 1-.4 0 .28.28 0 0 1 0-.4l7.98-7.98.6.6-7.98 7.78Z" />
            </svg>
          </span>
          <span>Services</span>
        </Link>
        <Link to="/bulk-order">
          <span className="mobile-reference-home__tab-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Zm2.24.72L12 11.6l6.76-3.38L12 4.85 5.24 8.22ZM5 9.85v5.42l6 3v-5.42l-6-3Zm8 8.42 6-3V9.85l-6 3v5.42Z" />
            </svg>
          </span>
          <span>Bulk Order</span>
        </Link>
      </nav>

      <div
        className="mobile-reference-home__hero"
        aria-label="Homepage banners"
        onTouchStart={handleBannerTouchStart}
        onTouchEnd={handleBannerTouchEnd}
      >
        <div
          className="mobile-reference-home__hero-track"
          style={{ transform: `translateX(-${activeBannerIndex * 100}%)` }}
        >
          {heroBanners.map((banner) => (
            <Link
              key={banner.id}
              className="mobile-reference-home__hero-slide"
              to={`/products?discover=1&view=collection${banner.slug ? `&promo=${encodeURIComponent(banner.slug)}` : ""}${banner.heading ? `&title=${encodeURIComponent(banner.heading)}` : ""}`}
            >
              <img
                src={resolveMediaUrl(banner.imageUrl)}
                alt={banner.heading?.trim() || "Homepage banner"}
              />
            </Link>
          ))}
        </div>
        {heroBanners.length > 1 ? (
          <div className="mobile-reference-home__hero-dots" aria-label="Banner carousel">
            {heroBanners.map((banner, index) => (
              <button
                key={banner.id}
                type="button"
                className={index === activeBannerIndex ? "is-active" : ""}
                onClick={() => setActiveBannerIndex(index)}
                aria-label={`Show banner ${index + 1}`}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="mobile-reference-home__offers">
        {offerTiles.map((tile) => (
          <div key={tile.label} className="mobile-reference-home__offer-tile">
            <span aria-hidden="true">
              <img src={resolveMediaUrl(tile.image)} alt="" loading="lazy" />
            </span>
            <strong>{tile.label}</strong>
          </div>
        ))}
      </div>

      <CategorySection
        categories={featuredCategories}
        title="Shop by Category"
        className="mobile-reference-home__categories"
        gridClassName="mobile-reference-home__category-grid"
      />

    </section>
  );
};

const BrandCardGrid: React.FC<{ brands: BrandLogo[]; title: string; sectionId?: string }> = ({
  brands,
  title,
  sectionId,
}) => (
  <section className="mobile-reference-home__categories mobile-reference-home__brand-section" aria-label={title} id={sectionId}>
    <div className="mobile-reference-home__section-heading">
      <h2>{title}</h2>
      <Link to="/products?discover=1&view=collection">
        See All
        <span aria-hidden="true">›</span>
      </Link>
    </div>
    <div className="desktop-brand-grid">
      {brands.slice(0, 8).map((brand) => (
        <Link
          key={brand.id}
          to={`/products?discover=1&view=collection&brand=${encodeURIComponent(brand.brandName)}&title=${encodeURIComponent(brand.brandName)}`}
        >
          <img src={resolveMediaUrl(brand.logoUrl)} alt={brand.brandName} />
        </Link>
      ))}
    </div>
  </section>
);

const formatWalletTransaction = (walletTransaction?: { type: "CREDIT" | "DEBIT" | "REFUND"; amount: number; description: string } | null) => {
  if (!walletTransaction) {
    return "No recent wallet transaction";
  }

  const sign = walletTransaction.type === "DEBIT" ? "-" : "+";
  const amount = `₹${Math.abs(walletTransaction.amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return `${sign}${amount} ${walletTransaction.description}`;
};

const HomeWalletCard: React.FC<{
  walletBalance: number;
  recentWalletTransaction?: { type: "CREDIT" | "DEBIT" | "REFUND"; amount: number; description: string } | null;
}> = ({ walletBalance, recentWalletTransaction }) => (
  <section className="shell section home-wallet-section">
    <div className="home-wallet-section__grid">
      <Link to="/wallet" className="home-wallet-card">
        <div className="home-wallet-card__copy">
          <span>My Wallet Balance</span>
          <strong>₹{walletBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          <span className="home-wallet-card__action">Add Money</span>
          <small>{formatWalletTransaction(recentWalletTransaction)}</small>
        </div>
        <span className="home-wallet-card__icon" aria-hidden="true">
          <img src={walletIcon} alt="" />
        </span>
      </Link>

      <aside className="home-wallet-cashback" aria-label="Cashback benefits">
        <span className="home-wallet-cashback__eyebrow">Cashback Zone</span>
        <strong>Earn up to 12% cashback</strong>
        <p>Use your wallet on repeat orders and watch cashback credits land faster.</p>
        <div className="home-wallet-cashback__meta">
          <img src={offerCashbackImage} alt="" loading="lazy" />
          <span>Wallet cashback updates automatically after eligible orders.</span>
        </div>
      </aside>
    </div>
  </section>
);

const ProductCarouselSection: React.FC<ProductCarouselSectionProps> = ({
  title,
  eyebrow,
  products,
  loading,
  seeAllTo,
}) => {
  const [isTabletViewport, setIsTabletViewport] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateViewport = () => {
      setIsTabletViewport(window.innerWidth >= 426 && window.innerWidth <= 768);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const { handleScroll, visibleCount, viewportRef } = useProgressiveProductReveal(
    products.length,
    isTabletViewport ? 4 : 6,
  );
  const visibleProducts = products.slice(0, visibleCount);

  return (
    <section className="shell section home-product-section">
      <div className="section-heading section-heading--carousel">
        <div>
          {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
          <h2>{title}</h2>
        </div>
        <div className="home-product-section__actions">
          <Link className="home-product-section__see-all" to={seeAllTo}>
            See All
            <span aria-hidden="true">›</span>
          </Link>
        </div>
      </div>
      {loading ? (
        <LoadingState />
      ) : visibleProducts.length ? (
        <div className="home-carousel">
          <div className="home-carousel__viewport" ref={viewportRef} onScroll={handleScroll}>
            <div className="product-grid product-grid--carousel">
              {visibleProducts.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="home-carousel home-carousel--empty">
          <div className="home-carousel__viewport">
            <div className="home-carousel__empty-state">
              <strong>No products match this section yet.</strong>
              <span>Add products with the right admin tags to populate it.</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const HomePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { bestSellerProducts, categories, loading, products } = useProducts();
  const [contentSections, setContentSections] = useState<HomepageSectionContent[]>([]);
  const [homepageBanners, setHomepageBanners] = useState<Banner[]>([]);
  const [offerCards, setOfferCards] = useState<Banner[]>([]);
  const [offerBanners, setOfferBanners] = useState<Banner[]>([]);
  const [brandLogos, setBrandLogos] = useState<BrandLogo[]>([]);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [recentWalletTransaction, setRecentWalletTransaction] = useState<{
    type: "CREDIT" | "DEBIT" | "REFUND";
    amount: number;
    description: string;
  } | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadHomepageSections = async () => {
      try {
        const contentResponse = await fetchHomepageSections();
        if (isMounted) {
          setContentSections(contentResponse);
        }
      } catch {
        if (isMounted) {
          setContentSections([]);
        }
      }
    };

    void loadHomepageSections();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadHomepageBanners = async () => {
      try {
        const response = await fetchBanners();
        if (isMounted) {
          setHomepageBanners(response.filter((banner) => banner.imageUrl));
        }
      } catch {
        if (isMounted) {
          setHomepageBanners([]);
        }
      }
    };

    void loadHomepageBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadOfferCards = async () => {
      try {
        const response = await fetchOfferCards();
        if (isMounted) {
          setOfferCards(response.filter((banner) => banner.imageUrl));
        }
      } catch {
        if (isMounted) {
          setOfferCards([]);
        }
      }
    };

    void loadOfferCards();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadOfferBanners = async () => {
      try {
        const response = await fetchOfferBanners();
        if (isMounted) {
          setOfferBanners(response.filter((banner) => banner.imageUrl));
        }
      } catch {
        if (isMounted) {
          setOfferBanners([]);
        }
      }
    };

    void loadOfferBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadBrandLogos = async () => {
      try {
        const response = await fetchBrandLogos();
        if (isMounted) {
          setBrandLogos(
            response
              .filter((brand) => brand.active && brand.logoUrl)
              .sort((left, right) => left.displayOrder - right.displayOrder),
          );
        }
      } catch {
        if (isMounted) {
          setBrandLogos([]);
        }
      }
    };

    void loadBrandLogos();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setWalletBalance(0);
      setRecentWalletTransaction(null);
      return;
    }

    let isMounted = true;
    setWalletBalance(user.walletBalance ?? 0);

    const loadWallet = async () => {
      try {
        const wallet = await fetchWallet();
        if (isMounted) {
          setWalletBalance(wallet.balance);
          setRecentWalletTransaction(wallet.transactions[0] ?? null);
        }
      } catch {
        if (isMounted) {
          setRecentWalletTransaction(null);
        }
      }
    };

    void loadWallet();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user]);

  const recentlyAddedProducts = useMemo(
    () =>
      [...products]
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
        .slice(0, 8),
    [products]
  );

  const categoryShowcase = useMemo(() => categories, [categories]);
  const visibleCategories = useMemo(
    () => categoryShowcase.filter((category) => category.name && category.count >= 0 && category.slug !== "services" && category.name.toLowerCase() !== "services"),
    [categoryShowcase],
  );
  const visibleBrands = useMemo(() => brandLogos.slice(0, 8), [brandLogos]);
  const resolvedOfferTiles = useMemo(
    () => {
      if (!offerCards.length) {
        return mobileOfferTiles;
      }

      const labelOrder = new Map(mobileOfferTiles.map((tile, index) => [tile.label.toLowerCase(), index]));

      return [...offerCards]
        .sort((left, right) => {
          const leftOrder = labelOrder.get(left.heading?.trim().toLowerCase() || "") ?? mobileOfferTiles.length;
          const rightOrder = labelOrder.get(right.heading?.trim().toLowerCase() || "") ?? mobileOfferTiles.length;
          return leftOrder - rightOrder || left.id - right.id;
        })
        .slice(0, mobileOfferTiles.length)
        .map((banner, index) => ({
          label: banner.heading?.trim() || mobileOfferTiles[index].label,
          image: banner.imageUrl || mobileOfferTiles[index].image,
        }));
    },
    [offerCards],
  );
  const resolvedHomepageSections = useMemo(() => {
    const sections = contentSections.length ? contentSections : defaultHomepageSections;

    const resolvedSections = sections
      .filter((section) => section.sectionKey !== "seasonal-picks")
      .map((section) => {
        if (section.sectionKey === "categories") {
          return {
            sectionKey: section.sectionKey,
            eyebrow: undefined,
            title: section.heading,
            products: [],
            kind: "categories" as const,
          };
        }

        if (section.sectionKey === "shop-by-brand") {
          return {
            sectionKey: section.sectionKey,
            eyebrow: undefined,
            title: section.heading,
            products: [],
            kind: "brands" as const,
          };
        }

        const sourceProducts =
          section.sectionKey === "best-selling"
            ? bestSellerProducts
            : section.sectionKey === "recently-added"
              ? recentlyAddedProducts
              : products;
        const sectionProducts = getHomepageSectionProducts(sourceProducts, section.sectionKey);
        return {
          sectionKey: section.sectionKey,
          eyebrow: undefined,
          title: section.heading,
          products: sectionProducts.slice(0, 6),
          kind: "products" as const,
        };
      });

    const offerCardSection = {
      sectionKey: "offer-cards",
      eyebrow: undefined,
      title: "Offer Cards",
      products: [],
      kind: "offer-cards" as const,
    };

    const offerBannerSection = {
      sectionKey: "offer-banners",
      eyebrow: undefined,
      title: "Offer Banners",
      products: [],
      kind: "offer-banners" as const,
    };

    const categoriesIndex = resolvedSections.findIndex((section) => section.sectionKey === "categories");
    if (categoriesIndex >= 0 && offerCards.length) {
      resolvedSections.splice(categoriesIndex, 0, offerCardSection);
    }

    const hardwareToolsIndex = resolvedSections.findIndex((section) => section.sectionKey === "hardware-tools");
    if (hardwareToolsIndex >= 0 && offerBanners.length) {
      resolvedSections.splice(hardwareToolsIndex + 1, 0, offerBannerSection);
    }

    return resolvedSections.filter((section) => section.kind !== "products" || section.products.length > 0);
  }, [bestSellerProducts, contentSections, offerBanners, offerCards, products, recentlyAddedProducts]);
  return (
    <>
      <DesktopHomePage
        categories={visibleCategories}
        brands={visibleBrands}
        sections={resolvedHomepageSections.map((section) => ({
          sectionKey: section.sectionKey,
          title: section.title,
          eyebrow: section.eyebrow,
          products: section.products,
          kind: section.kind,
          seeAllTo: buildSectionLink(section),
        }))}
        offerCards={offerCards}
        offerBanners={offerBanners}
        walletBalance={walletBalance}
        recentWalletTransaction={recentWalletTransaction}
        loading={loading}
      />
      <MobileReferenceHome
        categories={visibleCategories}
        offerTiles={resolvedOfferTiles}
        banners={homepageBanners}
      />

      <div className="legacy-home-content">
      <HomeWalletCard walletBalance={walletBalance} recentWalletTransaction={recentWalletTransaction} />

      {resolvedHomepageSections.map((section) => {
        if (section.kind === "categories") {
          return null;
        }

        if (section.kind === "brands") {
          return (
            <BrandCardGrid
              key={section.sectionKey}
              brands={visibleBrands}
              title={section.title}
              sectionId={section.sectionKey}
            />
          );
        }

        if (section.kind === "offer-cards") {
          return <OfferBannerStrip key={section.sectionKey} banners={offerCards} title={section.title} columns={4} />;
        }

        if (section.kind === "offer-banners") {
          return <OfferBannerStrip key={section.sectionKey} banners={offerBanners} title={section.title} columns={3} />;
        }

        return (
          <ProductCarouselSection
            key={section.sectionKey}
            eyebrow={section.eyebrow}
            title={section.title}
            products={section.products}
            loading={loading}
            seeAllTo={buildSectionLink(section)}
          />
        );
      })}
      <WhyShopSection />
      </div>
    </>
  );
};

export default HomePage;
